import { useCallback } from 'react';
import api from '@shared/services/api';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

/**
 * Custom hook for opening files from different API endpoints
 * @param {string} baseEndpoint - The base API endpoint (e.g., '/hr-job-description/file', '/job-description/file')
 * @returns {Function} openFile - Function to open a file by ID
 */
export const useOpenFiles = (baseEndpoint) => {
    const openFile = useCallback(async (id, errorMessage = 'Error opening file') => {
        if (!id) {
            notifyError('No file available');
            return;
        }

        try {
            const response = await api.get(`${baseEndpoint}/${id}`, {
                responseType: 'blob'
            });

            // Get content type from response headers
            const contentType = response.headers['content-type'] || 'application/pdf';
            
            // Create blob with the correct content type
            const blob = new Blob([response.data], { type: contentType });
            const fileUrl = URL.createObjectURL(blob);
            
            // Open file in new tab
            const newTab = window.open(fileUrl, '_blank');

            if (!newTab) {
                throw new Error('Failed to open new tab. Please check if popups are blocked.');
            }

            // Clean up the URL after some time
            setTimeout(() => {
                URL.revokeObjectURL(fileUrl);
            }, 1000);

            return response.data;
        } catch (error) {
            console.error(errorMessage, error);
            notifyError(errorMessage);
            throw error;
        }
    }, [baseEndpoint]);

    return openFile;
};

export default useOpenFiles;
